import React, { useState, useEffect, useRef } from 'react';
import { Edit3, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from '../ui/simple-toaster';
import { api, apiFormData } from '../../utils/api';
import { formatTime } from './record-keeper/formatters';

import SegmentTimeline from './record-keeper/SegmentTimeline';
import AuditLogSection from './record-keeper/AuditLogSection';
import AdjustCrossoverForm from './record-keeper/AdjustCrossoverForm';
import InsertEntryForm from './record-keeper/InsertEntryForm';
import RecalcResults from './record-keeper/RecalcResults';
import { SequenceWarnings, SequenceOkBadge } from './record-keeper/SequenceWarnings';
import NativeSelect from './record-keeper/NativeSelect';
import { checkInsertConflict } from './record-keeper/insertValidation';

const RecordKeeperPanel = ({ drivers = [], vehicles = [], lockedDriverId = null }) => {
  const [selectedDriver, setSelectedDriver] = useState(lockedDriverId);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  // Statutory rounding
  const [statutoryRounding, setStatutoryRounding] = useState(true);
  const [showStatutory, setShowStatutory] = useState(true);

  // Date/Time range
  const [startDate, setStartDate] = useState(() => {
    const d = new Date(Date.now() - 36 * 60 * 60 * 1000);
    return d.toISOString().split('T')[0];
  });
  const [startTime, setStartTime] = useState('00:00');
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [endTime, setEndTime] = useState('23:59');

  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(null);
  const [selectedTransition, setSelectedTransition] = useState(null);
  const [recalcResult, setRecalcResult] = useState(null);

  // Form states
  const [adjustTime, setAdjustTime] = useState('');
  const [insertStartTime, setInsertStartTime] = useState('');
  const [insertEndTime, setInsertEndTime] = useState('');
  const [insertDate, setInsertDate] = useState(new Date().toISOString().split('T')[0]);
  const [reason, setReason] = useState('');
  const [evidenceFile, setEvidenceFile] = useState(null);
  const [evidenceUrl, setEvidenceUrl] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Audit log
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [showAuditLog, setShowAuditLog] = useState(false);

  // Sequence validation
  const [sequenceIssues, setSequenceIssues] = useState([]);

  const fileInputRef = useRef(null);
  const insertFormRef = useRef(null);

  // Inline insert state (for segment-level quick-insert)
  const [inlineInsertSegId, setInlineInsertSegId] = useState(null);
  const [inlineInsertMode, setInlineInsertMode] = useState(null);
  const [inlineInsertDate, setInlineInsertDate] = useState(null);

  // Inline adjust state (for segment-level crossover adjust)
  const [inlineAdjustSegId, setInlineAdjustSegId] = useState(null);
  const [inlineAdjustTransition, setInlineAdjustTransition] = useState(null);
  const [inlineAdjustTimeVal, setInlineAdjustTimeVal] = useState('');

  // Fetch client statutory rounding setting
  useEffect(() => {
    api('/client-settings')
      .then(data => {
        const enabled = data?.settings?.statutory_rounding ?? true;
        setStatutoryRounding(enabled);
        setShowStatutory(enabled);
      })
      .catch(() => {});
  }, []);

  const isFRHV = () => {
    if (!selectedVehicle) return true;
    const vehicle = vehicles.find(v => v.vehicle_id === selectedVehicle);
    return vehicle?.vehicle_class === 'FRHV';
  };

  // ── Data Loading ──────────────────────────────────────────────
  const loadSegments = async () => {
    if (!selectedDriver) return;
    setLoading(true);
    try {
      const url = `/record-keeper/segments/${selectedDriver}?date=${startDate}&end_date=${endDate}`;
      const result = await api(url);

      let filteredSegments = result.segments || [];
      const rangeStart = new Date(`${startDate}T${startTime}:00Z`);
      const rangeEnd = new Date(`${endDate}T${endTime}:00Z`);

      filteredSegments = filteredSegments.filter(seg => {
        const segStart = new Date(seg.start_ts);
        const segEnd = seg.end_ts ? new Date(seg.end_ts) : new Date();
        const now = new Date();
        const isSentinel = segEnd && (segEnd - now) > 30 * 24 * 60 * 60 * 1000;
        const effectiveEnd = isSentinel ? new Date() : segEnd;
        return segStart < rangeEnd && effectiveEnd > rangeStart;
      });

      filteredSegments.sort((a, b) => new Date(a.start_ts) - new Date(b.start_ts));
      setSegments(filteredSegments);
      validateSequence(filteredSegments);
    } catch (err) {
      toast.error(err.message || 'Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  const validateSequence = (segs) => {
    const issues = [];
    const sorted = [...segs].sort((a, b) => new Date(a.start_ts) - new Date(b.start_ts));
    for (let i = 1; i < sorted.length; i++) {
      const prev = sorted[i - 1];
      const curr = sorted[i];
      if (prev.segment_type === curr.segment_type) {
        issues.push({
          index: i,
          message: `Consecutive ${curr.segment_type} segments: "${formatTime(prev.start_ts)}" and "${formatTime(curr.start_ts)}" — should alternate WORK/REST`,
          segment_ids: [prev.segment_id, curr.segment_id],
        });
      }
    }
    setSequenceIssues(issues);
  };

  const loadAuditLog = async () => {
    if (!selectedDriver) return;
    setAuditLoading(true);
    try {
      const result = await api(`/record-keeper/audit-log/${selectedDriver}?days=30`);
      setAuditLog(result.audit_log || []);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (selectedDriver && startDate) loadSegments();
  }, [selectedDriver, startDate, startTime, endDate, endTime]);

  useEffect(() => {
    if (selectedDriver && drivers.length > 0) {
      const driver = drivers.find(d => d.driver_id === selectedDriver);
      if (driver?.vehicle_id) setSelectedVehicle(driver.vehicle_id);
    }
  }, [selectedDriver, drivers]);

  // ── Handlers ──────────────────────────────────────────────────
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setEvidenceFile(file);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await apiFormData('/record-keeper/upload-evidence', formData);
      setEvidenceUrl(result.url);
      toast.success('Evidence uploaded');
    } catch (err) {
      toast.error('Failed to upload evidence: ' + (err.message || 'Unknown error'));
    }
  };

  const resetForm = () => {
    setEditMode(null);
    setSelectedTransition(null);
    setAdjustTime('');
    setInsertStartTime('');
    setInsertEndTime('');
    setInsertDate(new Date().toISOString().split('T')[0]);
    setReason('');
    setEvidenceFile(null);
    setEvidenceUrl(null);
    setInlineInsertSegId(null);
    setInlineInsertMode(null);
    setInlineAdjustSegId(null);
    setInlineAdjustTransition(null);
    setInlineAdjustTimeVal('');
  };

  const handleInlineAdjust = async ({ adjustTime: newTime, reason: r }) => {
    if (!inlineAdjustTransition) return;
    try {
      const result = await api('/record-keeper/adjust-crossover', {
        method: 'POST',
        body: JSON.stringify({
          driver_id: selectedDriver,
          segment_id: inlineAdjustTransition.segment_id,
          new_time: newTime,
          reason: r
        })
      });
      toast.success('Crossover adjusted successfully');
      setRecalcResult(result.compliance_recalc);
      setInlineAdjustSegId(null);
      setInlineAdjustTransition(null);
      setInlineAdjustTimeVal('');
      loadSegments();
    } catch (err) {
      const msg = err.message || 'Unknown error';
      if (msg.includes('overlap')) toast.error('Cannot adjust: would overlap adjacent segment');
      else if (msg.includes('after start')) toast.error('Invalid: end time must be after start');
      else toast.error(`Adjustment failed: ${msg}`);
    }
  };

  const handleInlineInsert = async ({ mode, date, startTime: st, endTime: et, reason: r, evidenceUrl: ev }) => {
    if (!selectedDriver) return;
    try {
      if (mode === 'insert_rest') {
        const result = await api('/record-keeper/insert-missed-entry', {
          method: 'POST',
          body: JSON.stringify({
            driver_id: selectedDriver,
            segment_type: 'REST',
            date, start_time: st, end_time: et,
            reason: r, evidence_url: ev
          })
        });
        toast.success(`Inserted ${result.inserted_rest?.duration_minutes || 0} min rest break`);
        setRecalcResult(result.compliance_recalc);
      } else {
        const result = await api('/record-keeper/insert-missed-work', {
          method: 'POST',
          body: JSON.stringify({
            driver_id: selectedDriver,
            date, work_start_time: st, rest_start_time: et,
            reason: r, evidence_url: ev
          })
        });
        toast.success('Inserted missed work time');
        setRecalcResult(result.compliance_recalc);
      }
      setInlineInsertSegId(null);
      setInlineInsertMode(null);
      loadSegments();
    } catch (err) {
      const msg = err.message || 'Unknown error';
      if (msg.includes('Cannot insert') && msg.includes('same type')) {
        toast.error('Type conflict: You are inserting into a segment of the same type. Use the opposite type.');
      } else if (msg.includes('Cannot insert') && (msg.includes('previous segment') || msg.includes('next segment'))) {
        toast.error('Sequence conflict: Adjacent segment is the same type. Diary must alternate WORK/REST.');
      } else {
        toast.error(`Insert failed: ${msg}`);
      }
    }
  };

  const handleAdjustCrossover = async () => {
    if (!selectedTransition || !adjustTime || !reason) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const result = await api('/record-keeper/adjust-crossover', {
        method: 'POST',
        body: JSON.stringify({
          driver_id: selectedDriver,
          segment_id: selectedTransition.segment_id,
          new_time: adjustTime,
          reason,
          evidence_url: evidenceUrl
        })
      });
      toast.success('Crossover adjusted successfully');
      setRecalcResult(result.compliance_recalc);
      resetForm();
      loadSegments();
    } catch (err) {
      const msg = err.message || 'Unknown error occurred';
      if (msg.includes('overlap')) toast.error('Cannot adjust: New time would overlap with adjacent segment');
      else if (msg.includes('End time must be after start')) toast.error('Invalid time: End time must be after start time');
      else if (msg.includes('Segment not found')) toast.error('Segment not found - it may have been modified. Please refresh.');
      else toast.error(`Adjustment failed: ${msg}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInsertMissedEntry = async () => {
    if (!selectedDriver || !insertStartTime || !insertEndTime || !reason) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      if (editMode === 'insert_rest') {
        const result = await api('/record-keeper/insert-missed-entry', {
          method: 'POST',
          body: JSON.stringify({
            driver_id: selectedDriver,
            segment_type: 'REST',
            date: insertDate,
            start_time: insertStartTime,
            end_time: insertEndTime,
            reason,
            evidence_url: evidenceUrl
          })
        });
        toast.success(`Inserted ${result.inserted_rest?.duration_minutes || 0} min rest break`);
        setRecalcResult(result.compliance_recalc);
      } else {
        const result = await api('/record-keeper/insert-missed-work', {
          method: 'POST',
          body: JSON.stringify({
            driver_id: selectedDriver,
            date: insertDate,
            work_start_time: insertStartTime,
            rest_start_time: insertEndTime,
            reason,
            evidence_url: evidenceUrl
          })
        });
        toast.success(`Inserted ${result.inserted_work?.duration_minutes || 0} min work time`);
        setRecalcResult(result.compliance_recalc);
      }
      resetForm();
      loadSegments();
    } catch (err) {
      const msg = err.message || 'Unknown error occurred';
      if (msg.includes('Cannot insert') && msg.includes('same type')) {
        toast.error('Type conflict: You are trying to insert into a segment of the same type. Use the opposite type instead.');
      } else if (msg.includes('Cannot insert') && msg.includes('previous segment is also')) {
        toast.error('Sequence conflict: The adjacent segment is the same type. Diary must alternate WORK/REST.');
      } else if (msg.includes('Cannot insert') && msg.includes('next segment is also')) {
        toast.error('Sequence conflict: The next segment is the same type. Diary must alternate WORK/REST.');
      } else if (msg.includes('No segment found') || msg.includes('No active diary')) {
        toast.error('No valid segment or diary found for that time range. Check the date and times.');
      } else {
        toast.error(`Insert failed: ${msg}`);
      }
    } finally {
      setSubmitting(false);
    }
  };

  // ── Compute insert conflict for header form ─────────────────────
  const insertConflict = (editMode === 'insert_work' || editMode === 'insert_rest') && insertDate && insertStartTime && insertEndTime
    ? checkInsertConflict(segments, editMode === 'insert_work' ? 'WORK' : 'REST', insertDate, insertStartTime, insertEndTime)
    : null;

  // ── Shared form props ─────────────────────────────────────────
  const formProps = {
    reason, setReason, evidenceFile, evidenceUrl,
    fileInputRef, onFileUpload: handleFileUpload, submitting,
    onCancel: resetForm
  };

  const driverOptions = drivers.map(function (d) {
    return { value: d.driver_id, label: (d.users?.first_name || '') + ' ' + (d.users?.last_name || '') };
  });

  const vehicleOptions = vehicles.map(function (v) {
    return { value: v.vehicle_id, label: (v.rego || v.registration) + ' (' + v.vehicle_class + ')' };
  });

  // ── Render ────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Driver, Vehicle & Date/Time Range Selection */}
      <div className="bg-slate-800/50 rounded-2xl border border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Edit3 className="w-5 h-5 text-emerald-400" />
            Record Keeper - Edit Work Diary
          </h2>
          {statutoryRounding && (
            <button
              onClick={() => setShowStatutory(!showStatutory)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors"
              data-testid="statutory-toggle"
              style={{
                background: showStatutory ? 'rgba(16, 185, 129, 0.15)' : 'rgba(100, 116, 139, 0.15)',
                borderColor: showStatutory ? 'rgba(16, 185, 129, 0.3)' : 'rgba(100, 116, 139, 0.3)',
                color: showStatutory ? '#6ee7b7' : '#94a3b8'
              }}
            >
              {showStatutory ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              {showStatutory ? 'Statutory' : 'Actual'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Select Driver</label>
            {lockedDriverId ? (
              <div className="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white text-sm">
                {driverOptions.find(d => d.value === lockedDriverId)?.label || 'You'}
              </div>
            ) : (
              <NativeSelect
                key={'driver-' + drivers.length}
                value={selectedDriver || ''}
                onChange={(val) => setSelectedDriver(val)}
                options={driverOptions}
                placeholder="Choose a driver..."
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                testId="select-driver"
              />
            )}
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              Vehicle (determines {isFRHV() ? 'FRHV' : 'Business'} rules)
            </label>
            <NativeSelect
              key={'vehicle-' + vehicles.length}
              value={selectedVehicle || ''}
              onChange={(val) => setSelectedVehicle(val)}
              options={vehicleOptions}
              placeholder="Select vehicle..."
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
              testId="select-vehicle"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm text-slate-400 mb-1">Start Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white cursor-pointer [color-scheme:dark]"
              data-testid="start-date" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Start Time</label>
            <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white [color-scheme:dark]"
              data-testid="start-time" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">End Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white cursor-pointer [color-scheme:dark]"
              data-testid="end-date" />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">End Time</label>
            <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)}
              className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white [color-scheme:dark]"
              data-testid="end-time" />
          </div>
        </div>

        {selectedVehicle && (
          <div className="mt-4">
            <span className={`px-3 py-1 rounded-full text-sm ${
              isFRHV() ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'
            }`}>
              {isFRHV() ? 'FRHV Rules Apply (HVN Law)' : 'Business Rules Apply'}
            </span>
          </div>
        )}
      </div>

      {/* Segments Timeline */}
      {selectedDriver && (
        <SegmentTimeline
          segments={segments} loading={loading} showStatutory={showStatutory}
          startDate={startDate} startTime={startTime} endDate={endDate} endTime={endTime}
          onRefresh={loadSegments}
          onToggleAuditLog={() => { setShowAuditLog(!showAuditLog); if (!showAuditLog) loadAuditLog(); }}
          showAuditLog={showAuditLog}
          onInsertWork={() => { setEditMode('insert_work'); setInsertDate(startDate); }}
          onInsertRest={() => { setEditMode('insert_rest'); setInsertDate(startDate); }}
          onEditTransition={(segId, transition, timeStr) => {
            setInlineAdjustSegId(segId);
            setInlineAdjustTransition(transition);
            setInlineAdjustTimeVal(timeStr);
          }}
          onInlineInsert={(segId, mode, segDate) => {
            setInlineInsertSegId(segId);
            setInlineInsertMode(mode);
            setInlineInsertDate(segDate);
          }}
          inlineInsertSegId={inlineInsertSegId}
          inlineInsertMode={inlineInsertMode}
          onCancelInlineInsert={() => { setInlineInsertSegId(null); setInlineInsertMode(null); }}
          onInlineSubmit={handleInlineInsert}
          inlineAdjustSegId={inlineAdjustSegId}
          inlineAdjustTransition={inlineAdjustTransition}
          inlineAdjustTime={inlineAdjustTimeVal}
          onInlineAdjustSubmit={handleInlineAdjust}
          onCancelInlineAdjust={() => { setInlineAdjustSegId(null); setInlineAdjustTransition(null); setInlineAdjustTimeVal(''); }}
        />
      )}

      {/* Sequence Validation */}
      {selectedDriver && <SequenceWarnings sequenceIssues={sequenceIssues} />}
      {selectedDriver && segments.length > 1 && sequenceIssues.length === 0 && <SequenceOkBadge />}

      {/* Audit Log */}
      {showAuditLog && selectedDriver && (
        <AuditLogSection auditLog={auditLog} auditLoading={auditLoading} onRefresh={loadAuditLog} />
      )}

      {/* Edit Forms - Insert via header dropdown (fallback) */}
      {(editMode === 'insert_work' || editMode === 'insert_rest') && (
        <div ref={insertFormRef}>
          <InsertEntryForm
            editMode={editMode}
            insertDate={insertDate} setInsertDate={setInsertDate}
            insertStartTime={insertStartTime} setInsertStartTime={setInsertStartTime}
            insertEndTime={insertEndTime} setInsertEndTime={setInsertEndTime}
            insertConflict={insertConflict}
            {...formProps} onSubmit={handleInsertMissedEntry}
          />
        </div>
      )}

      {/* Recalculation Results */}
      <RecalcResults recalcResult={recalcResult} />
    </div>
  );
};

export default RecordKeeperPanel;
